import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
    Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, CircularProgress, Alert, Box, Chip, IconButton, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid, Avatar,
    Card, CardContent, InputAdornment, MenuItem, Select, FormControl,
    ToggleButton, ToggleButtonGroup, LinearProgress, Rating, Tabs, Tab, Divider, Checkbox, FormControlLabel,
    Badge, Pagination, Stack
} from '@mui/material';
import {
    Visibility, Refresh, Person, Phone, DriveEta, Edit, Delete,
    Search, FilterList, Star, CheckCircle, Cancel, LocalShipping,
    TrendingUp, Verified, LocationOn, Description, History, Lock, LockOpen,
    EventAvailable, PersonAdd, CloudUpload
} from '@mui/icons-material';
import { getImageUrl } from '../utils/imageUrl';
import { customModal } from './CustomModal';

const OnboardImageField = ({ label, value, onChange }) => {
    const inputId = `driver-onboard-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    return (
    <Box sx={{ border: '1px dashed #cbd5e1', borderRadius: 2, p: 1.5, minHeight: 78, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{label} *</Typography>
            <Typography variant="caption" color={value ? 'success.main' : 'text.secondary'} noWrap>
                {value?.fileName || 'Image required'}
            </Typography>
        </Box>
        <Button component="label" htmlFor={inputId} variant="outlined" size="small" startIcon={<CloudUpload />} sx={{ flexShrink: 0, textTransform: 'none' }}>
            Upload
            <input id={inputId} name={inputId} aria-label={`${label} image`} hidden type="file" accept="image/*" onChange={(event) => {
                onChange(event.target.files?.[0]);
                event.target.value = '';
            }} />
        </Button>
    </Box>
    );
};

const DriverList = () => {
    const { t } = useTranslation();
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Filters & Sorting
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [availabilityFilter, setAvailabilityFilter] = useState('all');
    const [vehicleTypeFilter, setVehicleTypeFilter] = useState('all');
    const [ratingFilter, setRatingFilter] = useState('all');
    const [sortBy, setSortBy] = useState('rating_desc');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'
    const [page, setPage] = useState(1);
    const rowsPerPage = 8;

    // Modals
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [openProfileModal, setOpenProfileModal] = useState(false);
    const [profileTab, setProfileTab] = useState(0);
    const [openDocsModal, setOpenDocsModal] = useState(false);
    const [openTripsModal, setOpenTripsModal] = useState(false);
    const [driverTrips, setDriverTrips] = useState([]);
    const [loadingTrips, setLoadingTrips] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openOnboardModal, setOpenOnboardModal] = useState(false);
    const [savingOnboard, setSavingOnboard] = useState(false);
    const [onboardError, setOnboardError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [onboardZones, setOnboardZones] = useState([]);
    const [loadingOnboardZones, setLoadingOnboardZones] = useState(false);
    const emptyOnboardForm = {
        name: '', businessName: '', dob: '', phoneNumber: '', email: '', address: '',
        aadhar: '', pan: '', gst: '', licenseFront: null, licenseBack: null,
        vehicleType: 'truck', vehicleModel: '', tankCapacity: '', vehicleNumber: '',
        zoneId: '', basePrice: '', pricePer1000: '', vehicleRC: null,
        vehicleInsurance: null, photoFront: null, photoBack: null, photoSide: null,
        consentConfirmed: false
    };
    const [onboardForm, setOnboardForm] = useState(emptyOnboardForm);

    // Edit form data
    const [formData, setFormData] = useState({
        name: '',
        phoneNumber: '',
        aadhar: '',
        pan: '',
        address: '',
        status: 'active',
        isAvailable: true,
        vehicleType: 'Lorry',
        vehicleNumber: '',
        vehicleModel: ''
    });

    const fetchDrivers = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get('/admin/drivers');
            setDrivers(response.data);
        } catch (err) {
            setError(err.response?.data?.message || t('error') || 'Failed to fetch drivers');
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchDrivers();
    }, [fetchDrivers]);

    const openOnboardForm = async () => {
        setOnboardError('');
        setOnboardForm({ ...emptyOnboardForm });
        setOpenOnboardModal(true);
        setLoadingOnboardZones(true);
        try {
            const response = await axios.get('/admin/zones');
            const zones = Array.isArray(response.data) ? response.data : (response.data?.zones || []);
            setOnboardZones(zones.filter((zone) => zone.isActive !== false));
        } catch (err) {
            setOnboardZones([]);
            setOnboardError(err.response?.data?.message || 'Could not load service zones. Please retry.');
        } finally {
            setLoadingOnboardZones(false);
        }
    };

    const compressOnboardImage = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Could not read the selected image.'));
        reader.onload = () => {
            const image = new window.Image();
            image.onerror = () => reject(new Error('The selected file is not a valid image.'));
            image.onload = () => {
                const scale = Math.min(1, 1200 / Math.max(image.width, image.height));
                const canvas = document.createElement('canvas');
                canvas.width = Math.max(1, Math.round(image.width * scale));
                canvas.height = Math.max(1, Math.round(image.height * scale));
                canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
                resolve(canvas.toDataURL('image/jpeg', 0.72));
            };
            image.src = reader.result;
        };
        reader.readAsDataURL(file);
    });

    const handleOnboardImage = async (field, file) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setOnboardError('Please choose an image file.');
            return;
        }
        try {
            const dataUrl = await compressOnboardImage(file);
            setOnboardForm((current) => ({ ...current, [field]: { dataUrl, fileName: file.name } }));
            setOnboardError('');
        } catch (err) {
            setOnboardError(err.message || 'Could not process the selected image.');
        }
    };

    const isOnboardFormValid = (() => {
        const f = onboardForm;
        return Boolean(
            f.name.trim() && f.dob && f.phoneNumber.replace(/\D/g, '').length >= 10 &&
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()) && f.address.trim() &&
            f.aadhar.replace(/\D/g, '').length === 12 && f.pan.trim().length === 10 &&
            f.licenseFront && f.licenseBack && f.vehicleType && f.vehicleModel.trim() &&
            Number(f.tankCapacity) > 0 && f.vehicleNumber.trim() && f.zoneId &&
            Number(f.basePrice) > 0 && Number(f.pricePer1000) >= 0 && f.pricePer1000 !== '' &&
            f.vehicleRC && f.vehicleInsurance && f.photoFront && f.photoBack && f.photoSide &&
            f.consentConfirmed
        );
    })();

    const handleSubmitOnboard = async () => {
        if (!isOnboardFormValid) {
            setOnboardError('Complete all required driver and vehicle details, upload the required documents, and confirm consent.');
            return;
        }
        setSavingOnboard(true);
        setOnboardError('');
        try {
            const f = onboardForm;
            await axios.post('/admin/drivers/onboard', {
                name: f.name.trim(),
                businessName: f.businessName.trim(),
                dob: f.dob.split('-').reverse().join('/'),
                phoneNumber: f.phoneNumber.replace(/\D/g, ''),
                email: f.email.trim().toLowerCase(),
                address: f.address.trim(),
                aadhar: f.aadhar.replace(/\D/g, ''),
                pan: f.pan.trim().toUpperCase(),
                gst: f.gst.trim(),
                licenseFront: f.licenseFront.dataUrl,
                licenseBack: f.licenseBack.dataUrl,
                vehicleType: f.vehicleType,
                vehicleModel: f.vehicleModel.trim(),
                tankCapacity: Number(f.tankCapacity),
                vehicleNumber: f.vehicleNumber.trim().toUpperCase(),
                zoneId: f.zoneId,
                basePrice: Number(f.basePrice),
                pricePer1000: Number(f.pricePer1000),
                vehicleRC: f.vehicleRC.dataUrl,
                vehicleInsurance: f.vehicleInsurance.dataUrl,
                photoFront: f.photoFront.dataUrl,
                photoBack: f.photoBack.dataUrl,
                photoSide: f.photoSide.dataUrl
            });
            setOpenOnboardModal(false);
            setSuccessMessage('Driver onboarding submitted. The driver and vehicle are pending approval.');
            fetchDrivers();
        } catch (err) {
            setOnboardError(err.response?.data?.message || 'Could not add this driver. Please review the details and try again.');
        } finally {
            setSavingOnboard(false);
        }
    };

    // Action handlers
    const handleViewProfile = (driver) => {
        setSelectedDriver(driver);
        setProfileTab(0);
        setOpenProfileModal(true);
    };

    const handleViewDocs = (driver) => {
        setSelectedDriver(driver);
        setOpenDocsModal(true);
    };


    const handleViewTrips = async (driver) => {
        setSelectedDriver(driver);
        setOpenTripsModal(true);
        setLoadingTrips(true);
        try {
            const res = await axios.get(`/admin/drivers/${driver._id}/trips`);
            setDriverTrips(res.data.trips || []);
        } catch (e) {
            console.error('Failed to load driver trips', e);
            setDriverTrips([]);
        } finally {
            setLoadingTrips(false);
        }
    };

    const handleEditClick = (driver) => {
        setSelectedDriver(driver);
        setFormData({
            name: driver.name || '',
            phoneNumber: driver.phoneNumber || '',
            aadhar: driver.aadhar || '',
            pan: driver.pan || '',
            address: driver.address || '',
            status: driver.status || 'active',
            isAvailable: Boolean(driver.isAvailable),
            vehicleType: driver.vehicle?.type || 'Lorry',
            vehicleNumber: driver.vehicle?.number || '',
            vehicleModel: driver.vehicle?.model || ''
        });
        setOpenEditModal(true);
    };

    const handleToggleStatus = async (driver) => {
        const newStatus = driver.status === 'suspended' ? 'active' : 'suspended';
        const actionLabel = newStatus === 'suspended' ? 'Suspend' : 'Activate';
        const confirmed = await customModal.confirm(
            `${actionLabel} Driver`,
            `Are you sure you want to ${actionLabel.toLowerCase()} ${driver.name}?`,
            { type: newStatus === 'suspended' ? 'warning' : 'info', confirmText: actionLabel }
        );

        if (confirmed) {
            try {
                await axios.put(`/admin/drivers/${driver._id}`, { status: newStatus });
                fetchDrivers();
            } catch (err) {
                setError(`Failed to ${actionLabel.toLowerCase()} driver`);
            }
        }
    };

    const handleDelete = async (id) => {
        const confirmed = await customModal.confirm(
            "Delete Driver",
            "Are you sure you want to delete this driver? This action cannot be undone.",
            { type: 'error', confirmText: 'Delete' }
        );
        if (confirmed) {
            try {
                await axios.delete(`/admin/drivers/${id}`);
                fetchDrivers();
            } catch (err) {
                setError("Failed to delete driver");
            }
        }
    };

    const handleSaveEdit = async () => {
        try {
            await axios.put(`/admin/drivers/${selectedDriver._id}`, {
                name: formData.name,
                phoneNumber: formData.phoneNumber,
                aadhar: formData.aadhar,
                pan: formData.pan,
                address: formData.address,
                status: formData.status,
                isAvailable: formData.isAvailable,
                vehicle: {
                    type: formData.vehicleType,
                    number: formData.vehicleNumber,
                    model: formData.vehicleModel
                }
            });
            setOpenEditModal(false);
            fetchDrivers();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to update driver details");
        }
    };

    // Calculate Summary Stats
    const totalDrivers = drivers.length;
    const onlineDrivers = drivers.filter(d => d.status === 'active').length;
    const offlineDrivers = drivers.filter(d => d.status === 'inactive' || d.status === 'pending').length;
    const busyDrivers = drivers.filter(d => d.status === 'active' && !d.isAvailable).length;
    const availableDrivers = drivers.filter(d => d.status === 'active' && d.isAvailable).length;
    const verifiedDrivers = drivers.filter(d => d.isApproved || d.status === 'active').length;
    const avgRatingAll = drivers.length > 0
        ? (drivers.reduce((acc, d) => acc + (d.averageRating || 0), 0) / drivers.length).toFixed(1)
        : '0';
    const totalTripsToday = drivers.reduce((acc, d) => acc + (d.todayTrips || 0), 0);

    // Filtering logic
    const filteredDrivers = drivers.filter(driver => {
        // Search term
        const term = searchTerm.toLowerCase();
        const matchesSearch = !term ||
            (driver.name || '').toLowerCase().includes(term) ||
            (driver._id || '').toLowerCase().includes(term) ||
            (driver.phoneNumber || '').toLowerCase().includes(term) ||
            (driver.vehicle?.number || '').toLowerCase().includes(term);

        // Status Filter
        const matchesStatus = statusFilter === 'all' ||
            (statusFilter === 'online' && driver.status === 'active') ||
            (statusFilter === 'offline' && driver.status !== 'active');

        // Availability Filter
        const matchesAvailability = availabilityFilter === 'all' ||
            (availabilityFilter === 'available' && driver.isAvailable) ||
            (availabilityFilter === 'busy' && !driver.isAvailable);

        // Vehicle Type Filter
        const matchesVehicle = vehicleTypeFilter === 'all' ||
            (driver.vehicle?.type || '').toLowerCase() === vehicleTypeFilter.toLowerCase();

        // Verification Filter
        const matchesVerification = true; // filtered within profile modal tabs

        // Rating Filter
        const driverRating = driver.averageRating || 0;
        const matchesRating = ratingFilter === 'all' ||
            (ratingFilter === '4.5' && driverRating >= 4.5) ||
            (ratingFilter === '4.0' && driverRating >= 4.0) ||
            (ratingFilter === '3.5' && driverRating >= 3.5);

        return matchesSearch && matchesStatus && matchesAvailability && matchesVehicle && matchesVerification && matchesRating;
    });

    // Sorting logic
    const sortedDrivers = [...filteredDrivers].sort((a, b) => {
        if (sortBy === 'rating_desc') return (b.averageRating || 0) - (a.averageRating || 0);
        if (sortBy === 'trips_desc') return (b.totalTrips || 0) - (a.totalTrips || 0);
        if (sortBy === 'earnings_desc') return (b.totalEarnings || 0) - (a.totalEarnings || 0);
        if (sortBy === 'date_desc') return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        if (sortBy === 'name_asc') return (a.name || '').localeCompare(b.name || '');
        return 0;
    });

    // Pagination
    const pageCount = Math.ceil(sortedDrivers.length / rowsPerPage);
    const paginatedDrivers = sortedDrivers.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
                <CircularProgress size={50} thickness={4} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, backgroundColor: '#f8fafc', minHeight: '90vh' }}>
            {/* Page Title & Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
                        Driver Management Hub
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Monitor, manage, and audit all active service drivers in real-time
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        startIcon={<PersonAdd />}
                        onClick={openOnboardForm}
                        sx={{ borderRadius: 2, px: 2, py: 1.1, fontWeight: 800, textTransform: 'none' }}
                    >
                        Add Driver
                    </Button>
                    <Tooltip title="Refresh Data">
                        <IconButton onClick={fetchDrivers} color="primary" sx={{ bgcolor: 'white', border: '1px solid #e2e8f0' }}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {successMessage && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccessMessage('')}>{successMessage}</Alert>}

            {/* Dashboard Summary Cards (Top KPI Banner) */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3} lg={1.5}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #2563eb' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>TOTAL DRIVERS</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mt: 0.5 }}>{totalDrivers}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <Person fontSize="inherit" color="action" /> Registered
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3} lg={1.5}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #16a34a' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>ONLINE DRIVERS</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#16a34a', mt: 0.5 }}>{onlineDrivers}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <CheckCircle fontSize="inherit" color="success" /> Connected
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3} lg={1.5}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #64748b' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>OFFLINE DRIVERS</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#64748b', mt: 0.5 }}>{offlineDrivers}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <Cancel fontSize="inherit" color="action" /> Inactive / Pending
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3} lg={1.5}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #0284c7' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>AVAILABLE</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0284c7', mt: 0.5 }}>{availableDrivers}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <EventAvailable fontSize="inherit" color="info" /> Ready for dispatch
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3} lg={1.5}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #ea580c' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>BUSY DRIVERS</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#ea580c', mt: 0.5 }}>{busyDrivers}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <LocalShipping fontSize="inherit" color="warning" /> On active job
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3} lg={1.5}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #9333ea' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>VERIFIED</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#9333ea', mt: 0.5 }}>{verifiedDrivers}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <Verified fontSize="inherit" sx={{ color: '#9333ea' }} /> Docs Approved
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3} lg={1.5}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #eab308' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>AVG RATING</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#ca8a04', mt: 0.5 }}>⭐ {avgRatingAll}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <Star fontSize="inherit" sx={{ color: '#eab308' }} /> Customer Feedback
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3} lg={1.5}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: 'white', border: '1px solid #e2e8f0', borderLeft: '4px solid #0d9488' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>TRIPS TODAY</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0d9488', mt: 0.5 }}>{totalTripsToday}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <TrendingUp fontSize="inherit" color="success" /> Today's volume
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Filters, Search & View Controls Bar */}
            <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 4, bgcolor: 'white', border: '1px solid #e2e8f0' }}>
                <Grid container spacing={2} alignItems="center">
                    {/* Search Field */}
                    <Grid item xs={12} md={3.5}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by name, ID, phone, vehicle..."
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ color: '#64748b' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ bgcolor: '#f1f5f9', borderRadius: 2, '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
                        />
                    </Grid>

                    {/* Status Filter */}
                    <Grid item xs={6} sm={4} md={1.5}>
                        <FormControl fullWidth size="small">
                            <Select
                                value={statusFilter}
                                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                                displayEmpty
                                sx={{ bgcolor: '#f1f5f9', borderRadius: 2, '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, fontWeight: 600 }}
                            >
                                <MenuItem value="all">All Status</MenuItem>
                                <MenuItem value="online">Online</MenuItem>
                                <MenuItem value="offline">Offline</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Availability Filter */}
                    <Grid item xs={6} sm={4} md={1.5}>
                        <FormControl fullWidth size="small">
                            <Select
                                value={availabilityFilter}
                                onChange={(e) => { setAvailabilityFilter(e.target.value); setPage(1); }}
                                displayEmpty
                                sx={{ bgcolor: '#f1f5f9', borderRadius: 2, '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, fontWeight: 600 }}
                            >
                                <MenuItem value="all">All Availability</MenuItem>
                                <MenuItem value="available">Available</MenuItem>
                                <MenuItem value="busy">Busy / On Trip</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Vehicle Filter */}
                    <Grid item xs={6} sm={4} md={1.5}>
                        <FormControl fullWidth size="small">
                            <Select
                                value={vehicleTypeFilter}
                                onChange={(e) => { setVehicleTypeFilter(e.target.value); setPage(1); }}
                                displayEmpty
                                sx={{ bgcolor: '#f1f5f9', borderRadius: 2, '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, fontWeight: 600 }}
                            >
                                <MenuItem value="all">All Vehicles</MenuItem>
                                <MenuItem value="lorry">Lorry</MenuItem>
                                <MenuItem value="tractor">Tractor</MenuItem>
                                <MenuItem value="truck">Truck</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Rating Filter */}
                    <Grid item xs={6} sm={4} md={1.5}>
                        <FormControl fullWidth size="small">
                            <Select
                                value={ratingFilter}
                                onChange={(e) => { setRatingFilter(e.target.value); setPage(1); }}
                                displayEmpty
                                sx={{ bgcolor: '#f1f5f9', borderRadius: 2, '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, fontWeight: 600 }}
                            >
                                <MenuItem value="all">All Ratings</MenuItem>
                                <MenuItem value="4.5">⭐ 4.5 & Above</MenuItem>
                                <MenuItem value="4.0">⭐ 4.0 & Above</MenuItem>
                                <MenuItem value="3.5">⭐ 3.5 & Above</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Sort By Dropdown */}
                    <Grid item xs={6} sm={4} md={1.5}>
                        <FormControl fullWidth size="small">
                            <Select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                sx={{ bgcolor: '#f1f5f9', borderRadius: 2, '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, fontWeight: 600 }}
                            >
                                <MenuItem value="rating_desc">Highest Rated</MenuItem>
                                <MenuItem value="trips_desc">Most Trips</MenuItem>
                                <MenuItem value="earnings_desc">Top Earnings</MenuItem>
                                <MenuItem value="date_desc">Newest Joined</MenuItem>
                                <MenuItem value="name_asc">Name (A-Z)</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* View Switcher Toggle */}
                    <Grid item xs={6} sm={4} md={1} sx={{ textAlign: 'right' }}>
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, newMode) => newMode && setViewMode(newMode)}
                            size="small"
                            sx={{ bgcolor: '#f1f5f9', borderRadius: 2 }}
                        >
                            <ToggleButton value="grid" sx={{ px: 1.5 }}>
                                <FilterList fontSize="small" />
                            </ToggleButton>
                            <ToggleButton value="table" sx={{ px: 1.5 }}>
                                <History fontSize="small" />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>
                </Grid>
            </Paper>

            {/* Content Display: Card Grid vs Table View */}
            {sortedDrivers.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 3, py: 4, textAlign: 'center' }}>
                    No drivers match the selected search criteria and filters.
                </Alert>
            ) : viewMode === 'grid' ? (
                /* Card Grid View */
                <Grid container spacing={3}>
                    {paginatedDrivers.map((driver) => {
                        const isOnline = driver.status === 'active';
                        const isAvailable = driver.isAvailable;
                        const rating = driver.averageRating || 0;
                        const isTopRated = rating >= 4.5;
                        const totalTrips = driver.totalTrips || 0;
                        const completedTrips = driver.completedTrips || 0;
                        const completionRate = totalTrips > 0 ? Math.round((completedTrips / totalTrips) * 100) : 100;

                        return (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={driver._id}>
                                <Card elevation={0} sx={{
                                    borderRadius: 4,
                                    border: '1px solid #e2e8f0',
                                    transition: 'all 0.25s ease-in-out',
                                    '&:hover': {
                                        boxShadow: '0 12px 24px -10px rgba(0,0,0,0.12)',
                                        transform: 'translateY(-3px)',
                                        borderColor: '#cbd5e1'
                                    },
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}>
                                    {/* Top Status Accent Ribbon */}
                                    <Box sx={{
                                        height: 6,
                                        bgcolor: isOnline ? (isAvailable ? '#16a34a' : '#ea580c') : '#94a3b8'
                                    }} />

                                    <CardContent sx={{ p: 2.5 }}>
                                        {/* Avatar & Header Info */}
                                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                                            <Badge
                                                overlap="circular"
                                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                badgeContent={
                                                    <Box sx={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        bgcolor: isOnline ? '#16a34a' : '#94a3b8',
                                                        border: '2px solid white'
                                                    }} />
                                                }
                                            >
                                                <Avatar
                                                    src={driver.profilePhoto ? getImageUrl(driver.profilePhoto) : ''}
                                                    sx={{ width: 56, height: 56, bgcolor: '#2563eb', fontWeight: 700 }}
                                                >
                                                    {driver.name ? driver.name.charAt(0).toUpperCase() : 'D'}
                                                </Avatar>
                                            </Badge>

                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Typography variant="h6" noWrap sx={{ fontWeight: 800, color: '#0f172a', fontSize: 16 }}>
                                                        {driver.name || 'Unnamed Driver'}
                                                    </Typography>
                                                    {driver.isApproved && (
                                                        <Tooltip title="Verified Driver">
                                                            <Verified sx={{ fontSize: 16, color: '#2563eb' }} />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    ID: {driver._id.slice(-6).toUpperCase()}
                                                </Typography>

                                                {/* Status Badges */}
                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                                    <Chip
                                                        label={isOnline ? 'Online' : 'Offline'}
                                                        size="small"
                                                        color={isOnline ? 'success' : 'default'}
                                                        sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                                                    />
                                                    {isOnline && (
                                                        <Chip
                                                            label={isAvailable ? 'Available' : 'On Duty'}
                                                            size="small"
                                                            color={isAvailable ? 'info' : 'warning'}
                                                            sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                                                        />
                                                    )}
                                                    {isTopRated && (
                                                        <Chip
                                                            label="Top Rated"
                                                            size="small"
                                                            sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#fef08a', color: '#854d0e' }}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Divider sx={{ my: 1.5 }} />

                                        {/* Contact & Vehicle Details */}
                                        <Stack spacing={1} sx={{ mb: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Phone sx={{ fontSize: 16, color: '#64748b' }} />
                                                <Typography variant="body2" color="text.secondary">{driver.phoneNumber || '—'}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <DriveEta sx={{ fontSize: 16, color: '#64748b' }} />
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                                    {driver.vehicle?.type || 'Lorry'} • {driver.vehicle?.number || 'No Vehicle'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LocationOn sx={{ fontSize: 16, color: '#64748b' }} />
                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                    {driver.assignedZone || 'Central Service Zone'}
                                                </Typography>
                                            </Box>
                                        </Stack>

                                        {/* Performance Bar */}
                                        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>
                                                    COMPLETION RATE
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                                    {completionRate}% ({driver.completedTrips || 0}/{totalTrips})
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={completionRate}
                                                sx={{ height: 6, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#16a34a' } }}
                                            />
                                        </Box>

                                        {/* Ratings & Earnings Summary Pill */}
                                        <Grid container spacing={1} sx={{ mb: 2 }}>
                                            <Grid item xs={6}>
                                                <Box sx={{ p: 1, bgcolor: '#fffbeb', borderRadius: 2, textAlign: 'center', border: '1px solid #fef3c7' }}>
                                                    <Typography variant="caption" color="text.secondary" display="block">RATING</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#b45309' }}>
                                                        ⭐ {rating > 0 ? rating : '-'} <span style={{ fontSize: 10, color: '#78350f' }}>({driver.totalReviews || 0})</span>
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box sx={{ p: 1, bgcolor: '#f0fdf4', borderRadius: 2, textAlign: 'center', border: '1px solid #dcfce7' }}>
                                                    <Typography variant="caption" color="text.secondary" display="block">EARNINGS</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#15803d' }}>
                                                        ₹{(driver.totalEarnings || 0).toLocaleString('en-IN')}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        {/* Quick Action Buttons */}
                                        <Divider sx={{ mb: 1.5 }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                disableElevation
                                                onClick={() => handleViewProfile(driver)}
                                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}
                                            >
                                                View Profile
                                            </Button>

                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Tooltip title="Documents">
                                                    <IconButton size="small" onClick={() => handleViewDocs(driver)} sx={{ bgcolor: '#f1f5f9' }}>
                                                        <Description fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Trip History">
                                                    <IconButton size="small" onClick={() => handleViewTrips(driver)} sx={{ bgcolor: '#f1f5f9' }}>
                                                        <History fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Edit Driver">
                                                    <IconButton size="small" onClick={() => handleEditClick(driver)} sx={{ bgcolor: '#f1f5f9' }}>
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={driver.status === 'suspended' ? 'Activate' : 'Suspend'}>
                                                    <IconButton size="small" onClick={() => handleToggleStatus(driver)} sx={{ bgcolor: driver.status === 'suspended' ? '#fef2f2' : '#f1f5f9', color: driver.status === 'suspended' ? '#ef4444' : '#64748b' }}>
                                                        {driver.status === 'suspended' ? <LockOpen fontSize="small" /> : <Lock fontSize="small" />}
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            ) : (
                /* Enhanced Table View */
                <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <Table>
                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Driver</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Vehicle</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Zone</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Trips / Rate</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Earnings</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Rating</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569' }} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedDrivers.map((driver) => {
                                const isOnline = driver.status === 'active';
                                const rating = driver.averageRating || 0;
                                return (
                                    <TableRow key={driver._id} hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Avatar
                                                    src={driver.profilePhoto ? getImageUrl(driver.profilePhoto) : ''}
                                                    sx={{ width: 40, height: 40, bgcolor: '#2563eb', fontWeight: 700 }}
                                                >
                                                    {driver.name ? driver.name.charAt(0) : 'D'}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a' }}>{driver.name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{driver.phoneNumber}</Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{driver.vehicle?.number || '—'}</Typography>
                                            <Typography variant="caption" color="text.secondary">{driver.vehicle?.type || 'Lorry'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={isOnline ? (driver.isAvailable ? 'Online (Available)' : 'Online (On Job)') : 'Offline'}
                                                size="small"
                                                color={isOnline ? (driver.isAvailable ? 'success' : 'warning') : 'default'}
                                                sx={{ fontWeight: 700 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">{driver.assignedZone || 'Central Zone'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{driver.completedTrips || 0} completed</Typography>
                                            <Typography variant="caption" color="text.secondary">Total: {driver.totalTrips || 0}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#16a34a' }}>
                                                ₹{(driver.totalEarnings || 0).toLocaleString('en-IN')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#b45309' }}>
                                                ⭐ {rating > 0 ? rating : '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" color="primary" onClick={() => handleViewProfile(driver)}>
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" color="info" onClick={() => handleViewDocs(driver)}>
                                                <Description fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" color="secondary" onClick={() => handleEditClick(driver)}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" color="error" onClick={() => handleDelete(driver._id)}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Pagination Control */}
            {pageCount > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination
                        count={pageCount}
                        page={page}
                        onChange={(e, val) => setPage(val)}
                        color="primary"
                        shape="rounded"
                    />
                </Box>
            )}

            {/* ADMIN DRIVER ONBOARDING */}
            <Dialog
                open={openOnboardModal}
                onClose={() => !savingOnboard && setOpenOnboardModal(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, maxHeight: '92vh' } }}
            >
                <DialogTitle sx={{ pb: 1, fontWeight: 800 }}>
                    Add Driver
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Complete the same profile, license, vehicle and pricing details collected during driver onboarding.
                    </Typography>
                </DialogTitle>
                <DialogContent dividers sx={{ bgcolor: '#f8fafc' }}>
                    {onboardError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setOnboardError('')}>{onboardError}</Alert>}
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2 }}>Driver Profile</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}><TextField label="Full Name *" fullWidth value={onboardForm.name} onChange={(e) => setOnboardForm({ ...onboardForm, name: e.target.value })} /></Grid>
                            <Grid item xs={12} sm={6}><TextField label="Business Name (optional)" fullWidth value={onboardForm.businessName} onChange={(e) => setOnboardForm({ ...onboardForm, businessName: e.target.value })} /></Grid>
                            <Grid item xs={12} sm={6}><TextField label="Date of Birth *" type="date" fullWidth value={onboardForm.dob} onChange={(e) => setOnboardForm({ ...onboardForm, dob: e.target.value })} InputLabelProps={{ shrink: true }} inputProps={{ max: new Date().toISOString().slice(0, 10), min: '1950-01-01' }} /></Grid>
                            <Grid item xs={12} sm={6}><TextField label="Phone Number *" fullWidth value={onboardForm.phoneNumber} onChange={(e) => setOnboardForm({ ...onboardForm, phoneNumber: e.target.value.replace(/\D/g, '').slice(0, 13) })} /></Grid>
                            <Grid item xs={12} sm={6}><TextField label="Email Address *" type="email" fullWidth value={onboardForm.email} onChange={(e) => setOnboardForm({ ...onboardForm, email: e.target.value })} /></Grid>
                            <Grid item xs={12} sm={6}><TextField label="Aadhaar Number *" fullWidth value={onboardForm.aadhar} onChange={(e) => setOnboardForm({ ...onboardForm, aadhar: e.target.value.replace(/\D/g, '').slice(0, 12) })} /></Grid>
                            <Grid item xs={12} sm={6}><TextField label="PAN Number *" fullWidth value={onboardForm.pan} onChange={(e) => setOnboardForm({ ...onboardForm, pan: e.target.value.toUpperCase().slice(0, 10) })} /></Grid>
                            <Grid item xs={12} sm={6}><TextField label="GST (optional)" fullWidth value={onboardForm.gst} onChange={(e) => setOnboardForm({ ...onboardForm, gst: e.target.value.toUpperCase() })} /></Grid>
                            <Grid item xs={12}><TextField label="Residential Address *" fullWidth multiline minRows={2} value={onboardForm.address} onChange={(e) => setOnboardForm({ ...onboardForm, address: e.target.value })} /></Grid>
                        </Grid>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Driving License</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Upload both sides, as required by driver onboarding.</Typography>
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6}><OnboardImageField label="License Front" value={onboardForm.licenseFront} onChange={(file) => handleOnboardImage('licenseFront', file)} /></Grid>
                            <Grid item xs={12} sm={6}><OnboardImageField label="License Back" value={onboardForm.licenseBack} onChange={(file) => handleOnboardImage('licenseBack', file)} /></Grid>
                        </Grid>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2 }}>Vehicle & Service Pricing</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField select label="Vehicle Type *" fullWidth value={onboardForm.vehicleType} onChange={(e) => setOnboardForm({ ...onboardForm, vehicleType: e.target.value })}>
                                    <MenuItem value="truck">Lorry</MenuItem>
                                    <MenuItem value="tractor">Tractor</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}><TextField label="Vehicle Model *" fullWidth value={onboardForm.vehicleModel} onChange={(e) => setOnboardForm({ ...onboardForm, vehicleModel: e.target.value })} /></Grid>
                            <Grid item xs={12} sm={6}><TextField label="Tank Capacity (Litres) *" type="number" fullWidth value={onboardForm.tankCapacity} onChange={(e) => setOnboardForm({ ...onboardForm, tankCapacity: e.target.value })} inputProps={{ min: 1 }} /></Grid>
                            <Grid item xs={12} sm={6}><TextField label="Vehicle Registration Number *" fullWidth value={onboardForm.vehicleNumber} onChange={(e) => setOnboardForm({ ...onboardForm, vehicleNumber: e.target.value.toUpperCase() })} /></Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField select label="Service Zone *" fullWidth value={onboardForm.zoneId} onChange={(e) => setOnboardForm({ ...onboardForm, zoneId: e.target.value })} disabled={loadingOnboardZones || onboardZones.length === 0}>
                                    {onboardZones.map((zone) => <MenuItem key={zone._id} value={zone._id}>{zone.name}{zone.city ? ` · ${zone.city}` : ''}</MenuItem>)}
                                </TextField>
                                {loadingOnboardZones && <Typography variant="caption" color="text.secondary">Loading active service zones…</Typography>}
                                {!loadingOnboardZones && onboardZones.length === 0 && <Typography variant="caption" color="error">No active service zones available.</Typography>}
                            </Grid>
                            <Grid item xs={12} sm={6}><TextField label="Base Price (₹) *" type="number" fullWidth value={onboardForm.basePrice} onChange={(e) => setOnboardForm({ ...onboardForm, basePrice: e.target.value })} inputProps={{ min: 1 }} /></Grid>
                            <Grid item xs={12} sm={6}><TextField label="Price per 1000 Litres (₹) *" type="number" fullWidth value={onboardForm.pricePer1000} onChange={(e) => setOnboardForm({ ...onboardForm, pricePer1000: e.target.value })} inputProps={{ min: 0 }} /></Grid>
                        </Grid>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Vehicle Documents & Photos</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Required for vehicle verification, matching the mobile onboarding form.</Typography>
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6}><OnboardImageField label="RC Book" value={onboardForm.vehicleRC} onChange={(file) => handleOnboardImage('vehicleRC', file)} /></Grid>
                            <Grid item xs={12} sm={6}><OnboardImageField label="Insurance" value={onboardForm.vehicleInsurance} onChange={(file) => handleOnboardImage('vehicleInsurance', file)} /></Grid>
                            <Grid item xs={12} sm={4}><OnboardImageField label="Vehicle Front Photo" value={onboardForm.photoFront} onChange={(file) => handleOnboardImage('photoFront', file)} /></Grid>
                            <Grid item xs={12} sm={4}><OnboardImageField label="Vehicle Back Photo" value={onboardForm.photoBack} onChange={(file) => handleOnboardImage('photoBack', file)} /></Grid>
                            <Grid item xs={12} sm={4}><OnboardImageField label="Vehicle Side Photo" value={onboardForm.photoSide} onChange={(file) => handleOnboardImage('photoSide', file)} /></Grid>
                        </Grid>
                    </Paper>

                    <FormControlLabel
                        control={<Checkbox id="driver-onboard-consent" name="consentConfirmed" checked={onboardForm.consentConfirmed} onChange={(e) => setOnboardForm({ ...onboardForm, consentConfirmed: e.target.checked })} />}
                        label="I confirm the driver provided these details and documents with consent."
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenOnboardModal(false)} disabled={savingOnboard}>Cancel</Button>
                    <Button variant="contained" startIcon={savingOnboard ? <CircularProgress size={16} color="inherit" /> : <PersonAdd />} onClick={handleSubmitOnboard} disabled={!isOnboardFormValid || savingOnboard || loadingOnboardZones} sx={{ borderRadius: 2, px: 2.5 }}>
                        {savingOnboard ? 'Submitting…' : 'Submit for Approval'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 1. COMPREHENSIVE DRIVER PROFILE MODAL */}
            <Dialog open={openProfileModal} onClose={() => setOpenProfileModal(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
                {selectedDriver && (
                    <>
                        <DialogTitle sx={{ p: 3, pb: 1, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar
                                    src={selectedDriver.profilePhoto ? getImageUrl(selectedDriver.profilePhoto) : ''}
                                    sx={{ width: 64, height: 64, bgcolor: '#2563eb', fontWeight: 800, fontSize: 24 }}
                                >
                                    {selectedDriver.name ? selectedDriver.name.charAt(0) : 'D'}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>{selectedDriver.name}</Typography>
                                        {selectedDriver.isApproved && <Chip label="Verified" size="small" color="primary" sx={{ fontWeight: 700 }} />}
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Driver ID: {selectedDriver._id} • Joined: {new Date(selectedDriver.createdAt || Date.now()).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                    </Typography>
                                </Box>
                            </Box>

                            <Tabs value={profileTab} onChange={(e, val) => setProfileTab(val)} sx={{ mt: 2 }}>
                                <Tab label="Personal & Vehicle" />
                                <Tab label="Performance & Financials" />
                                <Tab label="Customer Reviews" />
                                <Tab label="Documents" />
                            </Tabs>
                        </DialogTitle>

                        <DialogContent sx={{ p: 3 }}>
                            {profileTab === 0 && (
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ p: 2.5, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #e2e8f0' }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2, color: '#0f172a' }}>
                                                Personal Details
                                            </Typography>
                                            <Stack spacing={1.5}>
                                                <Typography variant="body2"><b>Mobile:</b> {selectedDriver.phoneNumber || '—'}</Typography>
                                                <Typography variant="body2"><b>Email:</b> {selectedDriver.email || 'Not provided'}</Typography>
                                                <Typography variant="body2"><b>License No:</b> {selectedDriver.licenseNumber || 'TN-34-202300481'}</Typography>
                                                <Typography variant="body2"><b>Aadhar Card:</b> {selectedDriver.aadhar || '—'}</Typography>
                                                <Typography variant="body2"><b>PAN Card:</b> {selectedDriver.pan || '—'}</Typography>
                                                <Typography variant="body2"><b>Address:</b> {selectedDriver.address || '—'}</Typography>
                                                <Typography variant="body2"><b>Last Active:</b> {selectedDriver.updatedAt ? new Date(selectedDriver.updatedAt).toLocaleString() : 'Recently'}</Typography>
                                            </Stack>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ p: 2.5, bgcolor: '#eff6ff', borderRadius: 3, border: '1px solid #dbeafe' }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2, color: '#1e40af' }}>
                                                Vehicle Assignment
                                            </Typography>
                                            <Stack spacing={1.5}>
                                                <Typography variant="body2"><b>Vehicle Number:</b> {selectedDriver.vehicle?.number || '—'}</Typography>
                                                <Typography variant="body2"><b>Vehicle Type:</b> {selectedDriver.vehicle?.type || 'Lorry'}</Typography>
                                                <Typography variant="body2"><b>Model:</b> {selectedDriver.vehicle?.model || 'Tata 1613'}</Typography>
                                                <Typography variant="body2"><b>Tank Capacity:</b> {selectedDriver.vehicle?.tankCapacity || '10,000 Litres'}</Typography>
                                                <Typography variant="body2"><b>Operating Zone:</b> {selectedDriver.assignedZone || 'Central Zone'}</Typography>
                                            </Stack>
                                        </Box>
                                    </Grid>
                                </Grid>
                            )}

                            {profileTab === 1 && (
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={4}>
                                        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, textAlign: 'center', bgcolor: '#f0fdf4' }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>COMPLETED TRIPS</Typography>
                                            <Typography variant="h3" sx={{ fontWeight: 800, color: '#16a34a', my: 1 }}>{selectedDriver.completedTrips || 0}</Typography>
                                            <Typography variant="caption" color="text.secondary">Out of {selectedDriver.totalTrips || 0} total requests</Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, textAlign: 'center', bgcolor: '#fffbeb' }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>TOTAL EARNINGS</Typography>
                                            <Typography variant="h3" sx={{ fontWeight: 800, color: '#b45309', my: 1 }}>₹{(selectedDriver.totalEarnings || 0).toLocaleString('en-IN')}</Typography>
                                            <Typography variant="caption" color="text.secondary">Today: ₹{(selectedDriver.todayEarnings || 0).toLocaleString('en-IN')}</Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, textAlign: 'center', bgcolor: '#fef2f2' }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>CANCELED ORDERS</Typography>
                                            <Typography variant="h3" sx={{ fontWeight: 800, color: '#dc2626', my: 1 }}>{selectedDriver.canceledTrips || 0}</Typography>
                                            <Typography variant="caption" color="text.secondary">Cancellation rate: {selectedDriver.totalTrips ? Math.round(((selectedDriver.canceledTrips || 0) / selectedDriver.totalTrips) * 100) : 0}%</Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            )}

                            {profileTab === 2 && (
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 3 }}>
                                        <Typography variant="h3" sx={{ fontWeight: 800, color: '#b45309' }}>⭐ {selectedDriver.averageRating > 0 ? selectedDriver.averageRating : 'No Ratings'}</Typography>
                                        <Box>
                                            <Rating value={selectedDriver.averageRating || 0} precision={0.1} readOnly />
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Based on {selectedDriver.totalReviews || 0} customer reviews
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {selectedDriver.recentReviews && selectedDriver.recentReviews.length > 0 ? (
                                        <Stack spacing={2}>
                                            {selectedDriver.recentReviews.map((rev, idx) => (
                                                <Paper key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{rev.customerName}</Typography>
                                                        <Rating value={rev.rating} size="small" readOnly />
                                                    </Box>
                                                    <Typography variant="body2" color="text.secondary">{rev.comment || 'Great service provided.'}</Typography>
                                                </Paper>
                                            ))}
                                        </Stack>
                                    ) : (
                                        <Alert severity="info">No written reviews yet for this driver.</Alert>
                                    )}
                                </Box>
                            )}

                            {profileTab === 3 && (
                                <Grid container spacing={2}>
                                    {['licenseCardFront', 'licenseCardBack', 'vehicleRC', 'vehicleInsurance'].map(key => (
                                        <Grid item xs={12} sm={6} key={key}>
                                            <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 3, textAlign: 'center' }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                                    {key === 'licenseCardFront' ? 'License Front' : key === 'licenseCardBack' ? 'License Back' : key === 'vehicleRC' ? 'Vehicle RC' : 'Insurance'}
                                                </Typography>
                                                {selectedDriver[key] || selectedDriver.vehicle?.[key] ? (
                                                    <Box
                                                        component="img"
                                                        src={getImageUrl(selectedDriver[key] || selectedDriver.vehicle?.[key])}
                                                        sx={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 2, cursor: 'pointer' }}
                                                        onClick={() => window.open(getImageUrl(selectedDriver[key] || selectedDriver.vehicle?.[key]), '_blank')}
                                                    />
                                                ) : (
                                                    <Box sx={{ height: 160, bgcolor: '#f1f5f9', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Typography variant="caption" color="text.secondary">Document Not Uploaded</Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </DialogContent>

                        <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                            <Button onClick={() => setOpenProfileModal(false)} variant="contained" sx={{ borderRadius: 2, px: 4 }}>Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* 2. DOCUMENTS MODAL */}
            <Dialog open={openDocsModal} onClose={() => setOpenDocsModal(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>Driver Verification Documents</DialogTitle>
                <DialogContent dividers>
                    {selectedDriver && (
                        <Grid container spacing={2}>
                            {['licenseCardFront', 'licenseCardBack'].map(key => (
                                <Grid item xs={12} sm={6} key={key}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
                                        {key === 'licenseCardFront' ? 'Driver License (Front)' : 'Driver License (Back)'}
                                    </Typography>
                                    {selectedDriver[key] ? (
                                        <Box
                                            component="img"
                                            src={getImageUrl(selectedDriver[key])}
                                            sx={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 2, border: '1px solid #cbd5e1', cursor: 'pointer' }}
                                            onClick={() => window.open(getImageUrl(selectedDriver[key]), '_blank')}
                                        />
                                    ) : (
                                        <Alert severity="warning">Not Available</Alert>
                                    )}
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenDocsModal(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* 3. TRIP HISTORY MODAL */}
            <Dialog open={openTripsModal} onClose={() => setOpenTripsModal(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>Driver Trip History</DialogTitle>
                <DialogContent dividers>
                    {loadingTrips ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                    ) : driverTrips.length === 0 ? (
                        <Alert severity="info">No trip records found for this driver.</Alert>
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Booking ID</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Pickup Location</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {driverTrips.map(trip => (
                                        <TableRow key={trip._id}>
                                            <TableCell>{trip._id.slice(-6).toUpperCase()}</TableCell>
                                            <TableCell>{trip.customer?.name || 'Customer'}</TableCell>
                                            <TableCell>{trip.pickupAddress || '—'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={trip.status}
                                                    size="small"
                                                    color={trip.status === 'completed' ? 'success' : trip.status === 'cancelled' ? 'error' : 'info'}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>₹{trip.amount || 0}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenTripsModal(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* 4. EDIT DRIVER MODAL */}
            <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>Edit Driver Profile</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Full Name"
                            fullWidth
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                        <TextField
                            label="Phone Number"
                            fullWidth
                            value={formData.phoneNumber}
                            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                        />
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    label="Vehicle Number"
                                    fullWidth
                                    value={formData.vehicleNumber}
                                    onChange={(e) => setFormData({ ...formData, vehicleNumber: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Vehicle Type"
                                    select
                                    fullWidth
                                    SelectProps={{ native: true }}
                                    value={formData.vehicleType}
                                    onChange={(e) => setFormData({ ...formData, vehicleType: e.target.value })}
                                >
                                    <option value="Lorry">Lorry</option>
                                    <option value="Tractor">Tractor</option>
                                    <option value="Truck">Truck</option>
                                </TextField>
                            </Grid>
                        </Grid>
                        <TextField
                            label="Aadhar Number"
                            fullWidth
                            value={formData.aadhar}
                            onChange={(e) => setFormData({ ...formData, aadhar: e.target.value })}
                        />
                        <TextField
                            label="PAN Number"
                            fullWidth
                            value={formData.pan}
                            onChange={(e) => setFormData({ ...formData, pan: e.target.value })}
                        />
                        <TextField
                            label="Address"
                            fullWidth
                            multiline
                            rows={2}
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        />
                        <TextField
                            label="Account Status"
                            select
                            fullWidth
                            SelectProps={{ native: true }}
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        >
                            <option value="active">Active (Online)</option>
                            <option value="inactive">Inactive (Offline)</option>
                            <option value="suspended">Suspended</option>
                            <option value="pending">Pending Approval</option>
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setOpenEditModal(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveEdit} sx={{ borderRadius: 2, px: 3 }}>Save Changes</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DriverList;
